import { useCallback, useEffect, useMemo, useState } from 'react'
import { erc20Abi, type Address, maxUint256 } from 'viem'
import { useAccount, useReadContract, useWriteContract, useChainId, useConfig } from 'wagmi'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { TransactionLabel, type PermitSingleTransactionItem, permit2MutationOptions } from '@eulerxyz/wagmi-evc'
import { contractBook, isSupportedChainId } from '@eulerxyz/core-sdk'
import { Token } from '@/domain/types/Token'
import { permit2Abi } from 'abis/evm/permit2'

// NOTE: Permit2 address is chain-agnostic in practice, but wagmi-evc also supports chain-specific via contractBook.
export const PERMIT2_ADDRESS = '0x000000000022D473030F116dDEE9F6B43aC78BA3' as const

export type Permit2PreflightStatus = 'idle' | 'needs_approval' | 'approving' | 'needs_signature' | 'signing' | 'ready'

function stringifyBigInts<T>(value: T): any {
  if (typeof value === 'bigint') return value.toString()
  if (Array.isArray(value)) return value.map(stringifyBigInts)
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value as any).map(([k, v]) => [k, stringifyBigInts(v)]))
  }
  return value
}

export interface UsePermit2PreflightParams {
  /** Token the user will spend (tokenIn) */
  token: Token | undefined
  /** Amount (base units) the user will spend */
  amount: bigint | undefined
  /** Contract that will pull funds (vault or swapper) */
  spender: Address | undefined
  /** Enable/disable preflight */
  enabled: boolean
}

export interface UsePermit2PreflightResult {
  status: Permit2PreflightStatus
  needsApproval: boolean
  approvePermit2: (() => void) | undefined
  isApproving: boolean
  isApprovalConfirmed: boolean
  signPermit2: (() => void) | undefined
  isSigning: boolean
  permitTx: PermitSingleTransactionItem | null
  error: Error | null
}

/**
 * Euler-style Permit2 preflight:
 * 1) approve(token, Permit2) if needed
 * 2) sign Permit2 typed data for (token, amount, spender)
 * 3) produce a wagmi-evc Permit tx item that can be prepended to the EVC batch.
 */
export function usePermit2Preflight({
  token,
  amount,
  spender,
  enabled,
}: UsePermit2PreflightParams): UsePermit2PreflightResult {
  const { address } = useAccount()
  const chainId = useChainId()
  const config = useConfig()

  const tokenAddress = token?.address as Address | undefined

  // SECURITY: Permit2 spender must NEVER be the EVC address.
  // EVC.call() allows any authenticated caller to make arbitrary calls as msg.sender=EVC.
  // If spender=EVC, an attacker could call EVC.call(Permit2, attacker, 0, transferFrom(victim,...))
  // to drain any victim's tokens whose Permit2 allowance has spender=EVC.
  if (spender && isSupportedChainId(chainId)) {
    const evcAddress = contractBook.evc.address[chainId]
    if (evcAddress && spender.toLowerCase() === evcAddress.toLowerCase()) {
      throw new Error(
        `SECURITY: Permit2 spender must not be the EVC address (${evcAddress}). Use the vault or swapper address as spender instead. See: EVC.call() + Permit2 allowance drain vulnerability.`,
      )
    }
  }

  const queryClient = useQueryClient()

  // Check ERC20 approval to Permit2
  const {
    data: allowance,
    isLoading: isAllowanceLoading,
    error: allowanceError,
    queryKey: allowanceQueryKey,
  } = useReadContract({
    address: tokenAddress,
    abi: erc20Abi,
    functionName: 'allowance',
    args: address && tokenAddress ? [address, PERMIT2_ADDRESS] : undefined,
    query: { enabled: enabled && !!address && !!tokenAddress },
  })

  // Check Permit2 allowance (owner -> token -> spender)
  const {
    data: permit2AllowanceData,
    isLoading: isPermit2AllowanceLoading,
    error: permit2AllowanceError,
    queryKey: permit2AllowanceQueryKey,
  } = useReadContract({
    address: PERMIT2_ADDRESS,
    abi: permit2Abi,
    functionName: 'allowance',
    args: address && tokenAddress && spender ? [address, tokenAddress, spender] : undefined,
    query: { enabled: enabled && !!address && !!tokenAddress && !!spender },
  })

  const needsApproval = useMemo(() => {
    if (!enabled) return false
    if (!tokenAddress || !address || !amount) return false
    if (amount <= 0n) return false
    if (allowance === undefined) return true
    return allowance < amount
  }, [enabled, tokenAddress, address, amount, allowance])

  // Check if Permit2 allowance exists and is sufficient
  const hasPermit2Allowance = useMemo(() => {
    if (!enabled || !tokenAddress || !address || !spender || !amount) return false
    if (amount <= 0n) return false
    if (permit2AllowanceData === undefined) return false
    const [allowanceAmount, expiration] = permit2AllowanceData
    // Check if allowance is sufficient and not expired
    const now = BigInt(Math.floor(Date.now() / 1000))
    const isExpired = expiration > 0n && expiration < now
    return allowanceAmount >= amount && !isExpired
  }, [enabled, tokenAddress, address, spender, amount, permit2AllowanceData])

  const { writeContract, data: approvalTxHash, isPending: isApprovePending, error: approveError } = useWriteContract()
  const {
    isSuccess: isApprovalConfirmed,
    isLoading: isApprovalWaiting,
    error: approvalWaitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: approvalTxHash,
  })

  // Refetch allowances when approval is confirmed
  useEffect(() => {
    if (isApprovalConfirmed) {
      // Invalidate and refetch both ERC20 and Permit2 allowances
      if (allowanceQueryKey) {
        queryClient.invalidateQueries({ queryKey: allowanceQueryKey })
      }
      if (permit2AllowanceQueryKey) {
        queryClient.invalidateQueries({ queryKey: permit2AllowanceQueryKey })
      }
    }
  }, [isApprovalConfirmed, allowanceQueryKey, permit2AllowanceQueryKey, queryClient])

  const approvePermit2 = useCallback(() => {
    if (!tokenAddress || !address || !amount) return
    // Approve max to avoid repeated approvals
    writeContract({
      abi: erc20Abi,
      address: tokenAddress,
      functionName: 'approve',
      args: [PERMIT2_ADDRESS, maxUint256],
      chainId,
    } as any)
  }, [tokenAddress, address, amount, writeContract, chainId])

  // Sign Permit2 typed data (off-chain)
  // Use useMutation directly with the app's QueryClient to avoid context issues
  const mutationOptions = permit2MutationOptions(config)
  const { mutateAsync: permit2Async, isPending: isPermit2Pending, error: permit2Error } = useMutation(mutationOptions)

  const [permitTx, setPermitTx] = useState<PermitSingleTransactionItem | null>(null)

  // Clear permitTx when spender, amount, or token changes (signature is for specific params)
  // biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally want to reset when these specific params change
  useEffect(() => {
    setPermitTx(null)
  }, [spender, amount, tokenAddress])

  const signPermit2 = useCallback(() => {
    if (!enabled) return
    if (!address || !tokenAddress || !amount || !spender) return

    // Calculate expiration: 2 minutes from now (tight window to minimize front-running risk)
    // Permit2 allowance expires quickly — exact amount is consumed atomically in the batch
    const expirationTimestamp = Math.floor(Date.now() / 1000) + 2 * 60

    const sigDeadlineTimestamp = BigInt(Math.floor(Date.now() / 1000) + 2 * 60)

    void permit2Async({
      account: address,
      permit2Address: PERMIT2_ADDRESS,
      spender,
      sigDeadline: sigDeadlineTimestamp,
      details: {
        token: tokenAddress,
        amount,
        expiration: expirationTimestamp,
      },
    })
      .then((res: any) => {
        // Convert permit bigints to strings for the TransactionFactory
        const permitStringified = stringifyBigInts(res.permit)
        const tx: PermitSingleTransactionItem = {
          label: TransactionLabel.PERMIT,
          address: PERMIT2_ADDRESS,
          signature: res.signature,
          permit: permitStringified,
          asset: {
            address: tokenAddress,
            name: token?.name ?? token?.symbol ?? 'Token',
            symbol: token?.symbol ?? 'TOKEN',
            decimals: token?.decimals ?? 18,
            logoURI: token?.logoURI,
          },
          from: address,
          to: address,
          value: '0',
        }
        setPermitTx(tx)
      })
      .catch(() => {
        // error exposed via permit2Error
      })
  }, [enabled, address, tokenAddress, amount, spender, permit2Async, token])

  const status: Permit2PreflightStatus = useMemo(() => {
    if (!enabled || !tokenAddress || !address || !amount || !spender || amount <= 0n) return 'idle'
    // Still loading allowance data - show idle until we know what's needed
    if (isAllowanceLoading || isPermit2AllowanceLoading) return 'idle'
    if (needsApproval) return isApprovePending || isApprovalWaiting ? 'approving' : 'needs_approval'
    // If Permit2 allowance already exists and is sufficient, we're ready (no need to sign)
    if (hasPermit2Allowance) return 'ready'
    // Otherwise, need to sign Permit2 permit
    if (!permitTx) return isPermit2Pending ? 'signing' : 'needs_signature'
    return 'ready'
  }, [
    enabled,
    tokenAddress,
    address,
    amount,
    spender,
    isAllowanceLoading,
    isPermit2AllowanceLoading,
    needsApproval,
    isApprovePending,
    isApprovalWaiting,
    hasPermit2Allowance,
    permitTx,
    isPermit2Pending,
  ])

  const error =
    (allowanceError as any as Error | null) ||
    (permit2AllowanceError as any as Error | null) ||
    (approveError as any as Error | null) ||
    (approvalWaitError as any as Error | null) ||
    (permit2Error as any as Error | null) ||
    null

  return {
    status,
    needsApproval,
    approvePermit2: enabled && needsApproval ? approvePermit2 : undefined,
    isApproving: isApprovePending || isApprovalWaiting,
    isApprovalConfirmed,
    signPermit2: enabled && !needsApproval && !hasPermit2Allowance && !permitTx ? signPermit2 : undefined,
    isSigning: isPermit2Pending,
    permitTx,
    error,
  }
}
